import React from "react";
import { Sun, Moon, User, ChevronDown } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useExamCategory } from "../../contexts/ExamCategoryContext";
import { useUserProfile } from "../../contexts/UserProfileContext";
import { ProBadge, FreeOpenBadge } from "../ui/ProBadge";
import { useNavigate } from "react-router-dom";

/**
 * 簡素化ヘッダー（新レイアウト用）
 * Design Brief 6.2: ロゴ + 試験区分 + テーマ + ユーザー
 * MASTER.md: h-14, sticky top, z-20
 */
export const AppHeader: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { examInfo } = useExamCategory();
  const { isPro, isFreeOpenPeriod } = useUserProfile();
  const navigate = useNavigate();

  return (
    <header
      className="sticky top-0 z-20 bg-white dark:bg-slate-800
        border-b border-slate-200 dark:border-slate-700
        pt-[env(safe-area-inset-top)]"
    >
      <div className="max-w-2xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
        {/* Left: ロゴ + 試験区分 */}
        <div className="flex items-center gap-3 min-w-0">
          <img
            src="/logo.svg"
            alt="カクモン"
            className="w-8 h-8 rounded-lg flex-shrink-0"
          />
          <span
            className="text-sm font-semibold text-slate-900 dark:text-slate-100
              whitespace-nowrap flex-shrink-0"
          >
            カクモン
          </span>
          {isFreeOpenPeriod ? (
            <FreeOpenBadge className="flex-shrink-0" />
          ) : (
            isPro && <ProBadge className="flex-shrink-0" />
          )}
          {isAuthenticated && (
            <button
              onClick={() => navigate("/onboarding")}
              className="flex items-center gap-1 min-w-0 px-2 py-1 rounded-lg
                text-sm font-medium text-blue-500 dark:text-blue-400
                hover:bg-blue-50 dark:hover:bg-blue-900/20
                transition-colors duration-200 cursor-pointer"
              aria-label="試験区分を変更"
            >
              <span className="truncate">
                {examInfo.name} ({examInfo.shortName})
              </span>
              <ChevronDown size={14} className="flex-shrink-0" />
            </button>
          )}
        </div>

        {/* Right: テーマ切替 + ユーザー */}
        <div className="flex items-center gap-1">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-lg text-slate-500 dark:text-slate-400
              hover:bg-slate-100 dark:hover:bg-slate-700
              transition-colors duration-200 cursor-pointer"
            aria-label={theme === "dark" ? "ライトモード" : "ダークモード"}
          >
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          {isAuthenticated && (
            <button
              onClick={() => navigate("/mypage")}
              className="p-2 rounded-lg text-slate-500 dark:text-slate-400
                hover:bg-slate-100 dark:hover:bg-slate-700
                transition-colors duration-200 cursor-pointer"
              aria-label="マイページ"
            >
              <User size={18} />
            </button>
          )}
        </div>
      </div>
    </header>
  );
};
